// frontend/src/features/meetings/components/MeetingSettingsModal.js
import React, { useState } from 'react';
import calendarApi from '../api/calendarApi';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function normalizeSettings(settings, userEmail) {
  const base = { ...(settings || {}) };
  if (!base.connectedCalendars || base.connectedCalendars.length === 0) {
    base.connectedCalendars = [
      {
        provider: 'google',
        name: 'Google Calendar',
        account: null,
        status: 'disconnected',
        syncTarget: 'primary',
        availableCalendars: [],
        conflictCalendars: ['primary'],
        conferencingDefault: 'google_meet',
        lastSyncAt: null,
        health: null,
      },
      {
        provider: 'microsoft',
        name: 'Microsoft 365 Outlook',
        account: null,
        status: 'disconnected',
        syncTarget: 'default',
        availableCalendars: [],
        conflictCalendars: ['default'],
        conferencingDefault: 'teams',
        lastSyncAt: null,
        health: null,
      },
    ];
  } else {
    // Normalize incoming calendars: preserve connected status when status is 'connected'
    base.connectedCalendars = base.connectedCalendars.map((cal) => {
      const isConnected = cal.status === 'connected';
      const account = isConnected ? cal.account || userEmail || 'Connected Account' : null;
      return {
        ...cal,
        status: isConnected
          ? 'connected'
          : cal.status === 'needs_reauth' || cal.status === 'error'
            ? cal.status
            : 'disconnected',
        account,
        availableCalendars: isConnected
          ? cal.availableCalendars && cal.availableCalendars.length > 0
            ? cal.availableCalendars
            : [{ id: cal.syncTarget || 'primary', name: `Primary (${account})`, isPrimary: true }]
          : [],
        conflictCalendars: isConnected ? cal.conflictCalendars || ['primary'] : [],
      };
    });

    // Enforce mutual exclusivity: at most one calendar can be connected
    const connectedList = base.connectedCalendars.filter((cal) => cal.status === 'connected');
    if (connectedList.length > 1) {
      const activeProvider = [...connectedList].sort(
        (a, b) => new Date(b.lastSyncAt || 0).getTime() - new Date(a.lastSyncAt || 0).getTime()
      )[0].provider;
      base.connectedCalendars = base.connectedCalendars.map((c) =>
        c.provider === activeProvider
          ? c
          : { ...c, status: 'disconnected', account: null, lastSyncAt: null, health: null }
      );
    }
  }
  return base;
}

export default function MeetingSettingsModal({
  settings,
  meetingTypes = [],
  ownerEmail,
  userEmail,
  workspaceId = 'ws-primary',
  onClose,
  onSaveSettings,
}) {
  const effectiveEmail = userEmail || ownerEmail || '';
  const [localSettings, setLocalSettings] = useState(() =>
    normalizeSettings(settings, effectiveEmail)
  );

  // Keep localSettings in sync when settings prop updates
  React.useEffect(() => {
    if (settings) {
      setLocalSettings(normalizeSettings(settings, effectiveEmail));
    }
  }, [settings, effectiveEmail]);

  // Query verified database connection states on modal mount
  React.useEffect(() => {
    let isMounted = true;
    if (workspaceId && typeof calendarApi?.getConnections === 'function') {
      try {
        const promise = calendarApi.getConnections(workspaceId);
        if (promise && typeof promise.then === 'function') {
          promise
            .then((res) => {
              if (!isMounted || !res?.connections || !Array.isArray(res.connections)) return;
              let freshCals = res.connections.map((c) => ({
                provider: c.provider,
                name: c.provider === 'google' ? 'Google Calendar' : 'Microsoft 365 Outlook',
                status: c.status || 'disconnected',
                account: c.status === 'connected' ? c.account : null,
                availableCalendars: c.availableCalendars || [],
                conflictCalendars: c.conflictCalendars || [],
                lastSyncAt: c.lastSyncAt,
                conferencingDefault: c.provider === 'google' ? 'google_meet' : 'teams',
              }));
              const connectedApiList = freshCals.filter((c) => c.status === 'connected');
              if (connectedApiList.length > 1) {
                const activeProvider = [...connectedApiList].sort(
                  (a, b) =>
                    new Date(b.lastSyncAt || 0).getTime() - new Date(a.lastSyncAt || 0).getTime()
                )[0].provider;
                freshCals = freshCals.map((c) =>
                  c.provider === activeProvider
                    ? c
                    : { ...c, status: 'disconnected', account: null }
                );
              }
              setLocalSettings((prev) => ({
                ...prev,
                connectedCalendars: freshCals,
              }));
            })
            .catch(() => {});
        }
      } catch (e) {
        // Safe fallback
      }
    }
    return () => {
      isMounted = false;
    };
  }, [workspaceId]);

  const [disconnectTarget, setDisconnectTarget] = useState(null); // 'google' | 'microsoft' | null
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [outlookNotice] = useState(null);

  // Helper to get connection by provider
  const getConnection = (provider) => {
    const found = localSettings.connectedCalendars?.find((c) => c.provider === provider);
    if (found) {
      const isConnected = found.status === 'connected';
      const account = isConnected ? found.account || null : null;
      return {
        ...found,
        status: isConnected
          ? 'connected'
          : found.status === 'needs_reauth' || found.status === 'error'
            ? found.status
            : 'disconnected',
        account,
        availableCalendars: found.availableCalendars || [],
        conflictCalendars: found.conflictCalendars || [],
        conferencingDefault:
          found.conferencingDefault || (provider === 'google' ? 'google_meet' : 'teams'),
      };
    }
    return {
      provider,
      name: provider === 'google' ? 'Google Calendar' : 'Microsoft 365 Outlook',
      status: 'disconnected',
      account: null,
      availableCalendars: [],
      conflictCalendars: [],
      conferencingDefault: provider === 'google' ? 'google_meet' : 'teams',
    };
  };

  const rawGoogleConn = getConnection('google');
  const rawOutlookConn = getConnection('microsoft');

  // Strict mutual exclusivity in rendering: at most one calendar can ever be active
  const activeConnectedProvider =
    rawGoogleConn.status === 'connected' && rawOutlookConn.status === 'connected'
      ? new Date(rawOutlookConn.lastSyncAt || 0) >= new Date(rawGoogleConn.lastSyncAt || 0)
        ? 'microsoft'
        : 'google'
      : rawGoogleConn.status === 'connected'
        ? 'google'
        : rawOutlookConn.status === 'connected'
          ? 'microsoft'
          : null;

  const googleConn =
    activeConnectedProvider === 'google'
      ? rawGoogleConn
      : {
          ...rawGoogleConn,
          status: rawGoogleConn.status === 'connected' ? 'disconnected' : rawGoogleConn.status,
          account: rawGoogleConn.status === 'connected' ? null : rawGoogleConn.account,
        };

  const outlookConn =
    activeConnectedProvider === 'microsoft'
      ? rawOutlookConn
      : {
          ...rawOutlookConn,
          status: rawOutlookConn.status === 'connected' ? 'disconnected' : rawOutlookConn.status,
          account: rawOutlookConn.status === 'connected' ? null : rawOutlookConn.account,
        };

  const isGoogleConnected = activeConnectedProvider === 'google';
  const isOutlookConnected = activeConnectedProvider === 'microsoft';

  // Update a connection in local state and persist
  const updateConnection = (provider, patch) => {
    const prev = localSettings.connectedCalendars || [];
    const exists = prev.some((c) => c.provider === provider);
    const nextCalendars = exists
      ? prev.map((c) => (c.provider === provider ? { ...c, ...patch } : c))
      : [...prev, { provider, ...patch }];
    const nextSettings = { ...localSettings, connectedCalendars: nextCalendars };
    setLocalSettings(nextSettings);
    if (typeof onSaveSettings === 'function') {
      onSaveSettings(nextSettings);
    }
  };

  // Connect flow: Initiate real OAuth flow without faking connected status
  const handleConnect = async (provider) => {
    const isOtherConnected =
      provider === 'google'
        ? outlookConn.status === 'connected'
        : googleConn.status === 'connected';
    if (isOtherConnected) {
      return;
    }

    setConnectingProvider(provider);
    try {
      const res = await calendarApi.initiateOAuth(provider, workspaceId);
      if (res?.authUrl && res.authUrl.startsWith('http')) {
        // Real browser redirection to Google or Microsoft OAuth consent
        window.location.href = res.authUrl;
        return;
      }
      // If running in test/mock environment without real backend
      if (res?.authUrl && res.authUrl.startsWith('#mock-auth')) {
        const otherProvider = provider === 'google' ? 'microsoft' : 'google';
        const defaultAccount =
          provider === 'google'
            ? effectiveEmail || 'user@gmail.com'
            : `${(effectiveEmail || 'user').split('@')[0]}@outlook.com`;
        const nextCalendars = (localSettings.connectedCalendars || []).map((c) => {
          if (c.provider === provider) {
            return {
              ...c,
              status: 'connected',
              account: defaultAccount,
              lastSyncAt: new Date().toISOString(),
            };
          }
          if (c.provider === otherProvider) {
            return {
              ...c,
              status: 'disconnected',
              account: null,
            };
          }
          return c;
        });
        const nextSettings = { ...localSettings, connectedCalendars: nextCalendars };
        setLocalSettings(nextSettings);
        if (typeof onSaveSettings === 'function') {
          onSaveSettings(nextSettings);
        }
      }
    } catch (err) {
      console.warn(`[MeetingSettingsModal] Failed to initiate ${provider} OAuth:`, err?.message);
    } finally {
      setConnectingProvider(null);
    }
  };

  // Disconnect flow
  const handleConfirmDisconnect = async () => {
    if (!disconnectTarget) return;
    const provider = disconnectTarget;
    try {
      await calendarApi.disconnectConnection(workspaceId, provider);
    } catch (err) {
      console.warn('Disconnect error:', err);
    }
    updateConnection(provider, {
      status: 'disconnected',
      account: null,
      lastSyncAt: null,
      health: null,
    });
    setDisconnectTarget(null);
  };

  // Toggle day pill
  const toggleDay = (day) => {
    const current = localSettings.workingDays || [];
    const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
    setLocalSettings({ ...localSettings, workingDays: next });
  };

  // Save settings
  const handleSave = (e) => {
    e.preventDefault();
    onSaveSettings(localSettings);
    setSavedSuccess(true);
    setTimeout(() => {
      setSavedSuccess(false);
      onClose();
    }, 600);
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return (
          <span className="cal-status-badge connected">
            <i className="fas fa-check-circle"></i> Connected & Active
          </span>
        );
      case 'needs_reauth':
        return (
          <span className="cal-status-badge warning">
            <i className="fas fa-exclamation-triangle"></i> Needs Re-auth
          </span>
        );
      case 'syncing':
        return (
          <span className="cal-status-badge syncing">
            <i className="fas fa-sync-alt fa-spin"></i> Syncing...
          </span>
        );
      case 'error':
        return (
          <span className="cal-status-badge error">
            <i className="fas fa-times-circle"></i> Sync Error
          </span>
        );
      case 'disconnected':
      default:
        return <span className="cal-status-badge disconnected">Not connected</span>;
    }
  };

  return (
    <div className="meetings-modal-overlay" onClick={onClose}>
      <div className="meetings-modal-container settings-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="meetings-modal-header">
          <div className="meetings-modal-header-left">
            <div className="meetings-modal-header-icon">
              <i className="fas fa-calendar-alt"></i>
            </div>
            <div className="meetings-modal-title-wrap">
              <h2 className="meetings-modal-title">Calendar & Availability Settings</h2>
              <p className="meetings-modal-subtitle">
                Configure connected Google Calendar & Microsoft 365 Outlook, working hours, and
                meeting buffers.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="meetings-modal-close"
            onClick={onClose}
            title="Close modal"
            aria-label="Close modal"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSave} className="settings-modal-form">
          <div className="settings-modal-body">
            {/* Section 1: Connected Calendars */}
            <div className="settings-card-section">
              <div className="settings-section-header">
                <i className="fas fa-user-circle" />
                <span>Individual Calendar Connections</span>
              </div>
              <p className="settings-section-desc">
                Connect your individual work or Google Calendar to sync personal availability, block
                busy slots, and generate Google Meet links for meetings you host.
              </p>

              <div className="settings-calendars-list">
                {/* ── Google Calendar Card ── */}
                <div
                  className={`settings-cal-card ${googleConn.status === 'connected' ? 'connected' : ''}`}
                >
                  <div className="settings-cal-item">
                    <div className="cal-item-left">
                      <div className="cal-item-icon google">
                        <i className="fab fa-google"></i>
                      </div>
                      <div className="cal-item-info">
                        <div className="cal-item-title-row">
                          <strong className="cal-provider-title">
                            Google Calendar (Individual)
                          </strong>
                          {renderStatusBadge(googleConn.status)}
                        </div>
                        {googleConn.status === 'connected' ? (
                          <div className="cal-connected-meta">
                            <span className="cal-item-email">
                              <i className="far fa-envelope"></i> {googleConn.account}
                            </span>
                          </div>
                        ) : (
                          <span className="cal-item-desc">
                            Connect Google Calendar to sync meetings and block busy slots on booking
                            pages.
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="cal-item-right">
                      {googleConn.status === 'connected' ? (
                        <button
                          type="button"
                          className="cal-btn-disconnect"
                          onClick={() => setDisconnectTarget('google')}
                        >
                          Disconnect
                        </button>
                      ) : isOutlookConnected ? (
                        <button
                          type="button"
                          className="meetings-btn google-connect-btn cal-btn-disabled"
                          disabled
                          title="Only one calendar can be connected at a time. Disconnect Microsoft 365 Outlook first."
                        >
                          <i className="fas fa-ban"></i> Connect Google
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="meetings-btn google-connect-btn"
                          disabled={connectingProvider === 'google'}
                          onClick={() => handleConnect('google')}
                        >
                          {connectingProvider === 'google' ? (
                            <>
                              <i className="fas fa-spinner fa-spin"></i> Connecting...
                            </>
                          ) : (
                            <>
                              <i className="fab fa-google"></i> Connect Google
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  {!isGoogleConnected && isOutlookConnected && (
                    <div className="cal-item-notice">
                      <i className="fas fa-info-circle"></i>
                      <span>
                        Only one calendar can be connected at a time. Disconnect Microsoft 365
                        Outlook first to connect Google Calendar.
                      </span>
                    </div>
                  )}
                </div>

                {/* ── Microsoft 365 Outlook Card ── */}
                <div
                  className={`settings-cal-card ${outlookConn.status === 'connected' ? 'connected' : ''}`}
                >
                  <div className="settings-cal-item">
                    <div className="cal-item-left">
                      <div className="cal-item-icon microsoft">
                        <i className="fab fa-microsoft"></i>
                      </div>
                      <div className="cal-item-info">
                        <div className="cal-item-title-row">
                          <strong className="cal-provider-title">Microsoft 365 Outlook</strong>
                          {renderStatusBadge(outlookConn.status)}
                        </div>
                        {outlookConn.status === 'connected' ? (
                          <div className="cal-connected-meta">
                            <span className="cal-item-email">
                              <i className="far fa-envelope"></i> {outlookConn.account}
                            </span>
                          </div>
                        ) : (
                          <span className="cal-item-desc">
                            Connect Microsoft 365 Outlook to sync meetings and block busy slots on
                            booking pages.
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="cal-item-right">
                      {outlookConn.status === 'connected' ? (
                        <button
                          type="button"
                          className="cal-btn-disconnect"
                          onClick={() => setDisconnectTarget('microsoft')}
                        >
                          Disconnect
                        </button>
                      ) : isGoogleConnected ? (
                        <button
                          type="button"
                          className="meetings-btn ms-connect-btn cal-btn-disabled"
                          disabled
                          title="Only one calendar can be connected at a time. Disconnect Google Calendar first."
                        >
                          <i className="fas fa-ban"></i> Connect Outlook
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="meetings-btn ms-connect-btn"
                          disabled={connectingProvider === 'microsoft'}
                          onClick={() => handleConnect('microsoft')}
                        >
                          {connectingProvider === 'microsoft' ? (
                            <>
                              <i className="fas fa-spinner fa-spin"></i> Connecting...
                            </>
                          ) : (
                            <>
                              <i className="fab fa-microsoft"></i> Connect Outlook
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  {!isOutlookConnected && isGoogleConnected && (
                    <div className="cal-item-notice">
                      <i className="fas fa-info-circle"></i>
                      <span>
                        Only one calendar can be connected at a time. Disconnect Google Calendar
                        first to connect Microsoft 365 Outlook.
                      </span>
                    </div>
                  )}
                  {outlookNotice && (
                    <div className="cal-item-dev-notice">
                      <i className="fas fa-info-circle"></i>
                      <span>{outlookNotice}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section 2: Host Working Hours */}
            <div className="settings-card-section">
              <div className="settings-section-header">
                <i className="far fa-clock" />
                <span>Host Working Hours</span>
              </div>

              {/* Working Days Row */}
              <div className="working-days-bar">
                {DAYS_OF_WEEK.map((day) => {
                  const isSelected = (localSettings.workingDays || []).includes(day);
                  return (
                    <button
                      type="button"
                      key={day}
                      className={`day-pill-btn ${isSelected ? 'active' : ''}`}
                      onClick={() => toggleDay(day)}
                    >
                      {day.slice(0, 3)}
                    </button>
                  );
                })}
              </div>

              {/* Start, End */}
              <div className="settings-hours-row">
                <div className="form-group-clean">
                  <label className="form-label-clean">Day Start Time</label>
                  <input
                    type="time"
                    value={localSettings.workingHoursStart || '09:00'}
                    onChange={(e) =>
                      setLocalSettings({ ...localSettings, workingHoursStart: e.target.value })
                    }
                    className="form-input-clean"
                  />
                </div>

                <div className="form-group-clean">
                  <label className="form-label-clean">Day End Time</label>
                  <input
                    type="time"
                    value={localSettings.workingHoursEnd || '18:00'}
                    onChange={(e) =>
                      setLocalSettings({ ...localSettings, workingHoursEnd: e.target.value })
                    }
                    className="form-input-clean"
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Buffers & Scheduling Limits */}
            <div className="settings-card-section">
              <div className="settings-section-header">
                <i className="fas fa-hourglass-half" />
                <span>Buffers & Scheduling Limits</span>
              </div>

              <div className="settings-buffers-grid">
                <div className="form-group-clean">
                  <label className="form-label-clean">Buffer Before Call</label>
                  <select
                    value={localSettings.bufferBeforeMinutes ?? 15}
                    onChange={(e) =>
                      setLocalSettings({
                        ...localSettings,
                        bufferBeforeMinutes: Number(e.target.value),
                      })
                    }
                    className="form-input-clean"
                  >
                    <option value={0}>0 minutes</option>
                    <option value={5}>5 minutes</option>
                    <option value={10}>10 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                  </select>
                </div>

                <div className="form-group-clean">
                  <label className="form-label-clean">Buffer After Call</label>
                  <select
                    value={localSettings.bufferAfterMinutes ?? 15}
                    onChange={(e) =>
                      setLocalSettings({
                        ...localSettings,
                        bufferAfterMinutes: Number(e.target.value),
                      })
                    }
                    className="form-input-clean"
                  >
                    <option value={0}>0 minutes</option>
                    <option value={5}>5 minutes</option>
                    <option value={10}>10 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="settings-modal-footer">
            <button type="button" className="meetings-btn meetings-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="meetings-btn meetings-btn-primary">
              {savedSuccess ? (
                <>
                  <i className="fas fa-check"></i>
                  <span>Saved!</span>
                </>
              ) : (
                'Save Availability & Rules'
              )}
            </button>
          </div>
        </form>

        {/* Safe Disconnect Confirmation Dialog */}
        {disconnectTarget && (
          <div className="cal-disconnect-overlay" onClick={() => setDisconnectTarget(null)}>
            <div className="cal-disconnect-modal" onClick={(e) => e.stopPropagation()}>
              <div className="disconnect-modal-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h3 className="disconnect-modal-title">
                Disconnect{' '}
                {disconnectTarget === 'google' ? 'Google Calendar' : 'Microsoft 365 Outlook'}?
              </h3>
              <p className="disconnect-modal-text">
                Your past meetings and CRM activity logs will remain safely stored in Lead Huntrix.
                However, live two-way sync, webhook event delivery, and external free/busy conflict
                checking will stop immediately.
              </p>
              <div className="disconnect-modal-actions">
                <button
                  type="button"
                  className="meetings-btn meetings-btn-secondary"
                  onClick={() => setDisconnectTarget(null)}
                >
                  Keep Connected
                </button>
                <button
                  type="button"
                  className="meetings-btn meetings-btn-danger"
                  onClick={handleConfirmDisconnect}
                >
                  Confirm Disconnect
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
